// @refresh reset
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import Swal from "sweetalert2";
import api from "../../../utils/axios";

export default function ContractSignPage() {
  const { id } = useParams();

  const [password, setPassword] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [contract, setContract] = useState(null);
  const [remark, setRemark] = useState("");
  const [loading, setLoading] = useState(true);
  const [alreadySigned, setAlreadySigned] = useState(false);

  const sigRef = useRef(null);

  //  โหลดข้อมูล
  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/get-contract/${id}`);
        if (res.data.status === "OK" && res.data.header) {
          setContract(res.data.header);

          //  ถ้าเซ็นแล้ว ปิดการเซ็น
          if (res.data.header.Sign_By && res.data.header.Sign_Date) {
            setAlreadySigned(true);
          }
        }
      } catch {
        Swal.fire("ผิดพลาด!", "โหลดข้อมูลไม่สำเร็จ", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const signatureUrl = `${process.env.REACT_APP_API_URL}/api/contract-sign/file/${id}?t=${Date.now()}`;

  const pdfUrl = `${process.env.REACT_APP_API_REPORT_URL}/contract?P_DOC_ID=${id}&P_CONDITION=ต้นฉบับ#view=FitH&zoom=page-width&toolbar=0&navpanes=0&scrollbar=0&ts=${Date.now()}`;

  const formatThaiDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const months = [
      "มกราคม","กุมภาพันธ์","มีนาคม","เมษายน","พฤษภาคม","มิถุนายน",
      "กรกฎาคม","สิงหาคม","กันยายน","ตุลาคม","พฤศจิกายน","ธันวาคม"];
    return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear() + 543}`;
  };

  //  verify password
  const handleVerify = async () => {
    if (!password.trim()) {
      return Swal.fire("กรุณากรอกรหัสผ่าน", "", "warning");
    }

    try {
      const res = await api.post(`/verify-contract-password/${id}`, { password });

      if (res.data.status === "OK") {
        setIsVerified(true);
        // Swal.fire({
        //   icon: "success",
        //   title: "สำเร็จ!",
        //   text: "รหัสถูกต้อง",
        //   timer: 1500,
        //   showConfirmButton: false,
        // });

      } else {
        Swal.fire("ผิดพลาด", "รหัสผ่านไม่ถูกต้อง", "error");
      }
    } catch {
      Swal.fire("ผิดพลาด!", "ตรวจสอบรหัสผ่านไม่สำเร็จ", "error");
    }
  };

  //  reset canvas
  const clearSignature = () => {
    if (sigRef.current) sigRef.current.clear();
  };

  //  submit signature
  function trimCanvas(canvas) {
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h).data;

    let top = null, bottom = null, left = null, right = null;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const index = (y * w + x) * 4;
        if (img[index + 3] > 0) {
          if (top === null) top = y;
          bottom = y;
          if (left === null || x < left) left = x;
          if (right === null || x > right) right = x;
        }
      }
    }

    if (top === null) return canvas;

    const newW = right - left + 1;
    const newH = bottom - top + 1;

    const trimmed = document.createElement('canvas');
    trimmed.width = newW;
    trimmed.height = newH;
    const trimmedCtx = trimmed.getContext('2d');

    //  ใส่พื้นหลังขาว ให้ JPG ไม่โปร่งใส
    trimmedCtx.fillStyle = "#ffffff";
    trimmedCtx.fillRect(0, 0, newW, newH);

    trimmedCtx.drawImage(canvas, left, top, newW, newH, 0, 0, newW, newH);

    return trimmed;
  }

  const handleSubmitSignature = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      return Swal.fire("กรุณาเซ็นก่อน", "", "warning");
    }

    const originalCanvas = sigRef.current.getCanvas();
    const trimmedCanvas = trimCanvas(originalCanvas);

    //  ขนาดมาตรฐาน 2 x 0.7 นิ้ว @ 96 DPI
    const targetW = 192; // 2" * 96 dpi
    const targetH = 70;  // 0.7" * 96 dpi

    //  สร้าง canvas ใหม่สำหรับ size ที่ต้องการ
    const outputCanvas = document.createElement("canvas");
    outputCanvas.width = targetW;
    outputCanvas.height = targetH;
    const ctx = outputCanvas.getContext("2d");

    //  พื้นหลังขาว
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, targetW, targetH);

    //  คำนวณสัดส่วนเพื่อให้ลายเซ็นอยู่ตรงกลาง
    const scale = Math.min(
      targetW / trimmedCanvas.width,
      targetH / trimmedCanvas.height
    );

    const drawW = trimmedCanvas.width * scale;
    const drawH = trimmedCanvas.height * scale;

    const dx = (targetW - drawW) / 2;
    const dy = (targetH - drawH) / 2;

    ctx.drawImage(trimmedCanvas, dx, dy, drawW, drawH);

    //  แปลงเป็น JPG ส่งไป backend
    outputCanvas.toBlob(async (blob) => {
      const formData = new FormData();
      formData.append("signature", blob, `sign_${Date.now()}.jpg`);
      formData.append("password", password);
      formData.append("remark", remark);

      try {
        const res = await api.post(`/contract-sign/${id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (res.data.status === "OK") {
          window.location.reload();
        } else {
          Swal.fire("ผิดพลาด!", res.data.message, "error");
        }
      } catch (err) {
        Swal.fire("ผิดพลาด!", "บันทึกลายเซ็นไม่สำเร็จ", "error");
      }
    }, "image/jpeg", 0.9);
  };



  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen text-gray-500">
        กำลังโหลดข้อมูล...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center py-10">

      {!isVerified ? (
        <div className="bg-white shadow-xl rounded-xl p-8 w-[400px] max-w-[90%]">
          <h2 className="text-xl font-bold text-center mb-6 text-[#E13F57]">เข้าสู่หน้าลงนาม</h2>

          <input
            type="password"
            placeholder="กรอกรหัสผ่านสัญญา"
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            className="w-full h-14 border rounded-xl px-4 text-lg focus:outline-none focus:ring-2 focus:ring-[#E13F57]"
          />

          <button
            onClick={handleVerify}
            className="bg-[#E13F57] hover:bg-[#c8304b] text-white text-lg font-medium w-full h-14 rounded-xl mt-5"
          >
            ยืนยัน
          </button>
        </div>

      ) : (
        <div className="shadow-xl bg-white rounded-xl w-[950px] max-w-full" style={{ padding: 0, overflow: "hidden" }}>
          <iframe
            src={pdfUrl}
            title="contract_pdf"
            style={{ width: "100%", height: "1150px", border: "none" }}
          />

          <div className="p-6 border-t border-gray-200">
            <h3 className="text-center font-bold text-lg mb-3">ลายเซ็นผู้ลงนาม</h3>

            {!alreadySigned ? (
              <>
                <SignatureCanvas
                  ref={sigRef}
                  penColor="black"
                  canvasProps={{
                    width: 700,
                    height: 200,
                    className: "border border-gray-400 rounded-md bg-gray-50 shadow mx-auto",
                  }}
                />

                <div className="text-center mt-3">
                  <button onClick={clearSignature} className="bg-gray-500 text-white px-5 py-2 rounded-lg">
                    ล้างลายเซ็น
                  </button>
                </div>

                <textarea
                  rows={3}
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="หมายเหตุเพิ่มเติม..."
                  className="w-full border rounded-lg p-3 mt-6"
                />

                <div className="flex justify-center mt-6">
                  <button
                    onClick={handleSubmitSignature}
                    className="bg-[#E13F57] hover:bg-[#c8304b] text-white px-8 py-3 rounded-lg font-medium text-lg"
                  >
                    ยืนยันลงนาม
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center">
                <img
                  src={signatureUrl}
                  alt="signed"
                  className="border rounded bg-white shadow-md mx-auto"
                  style={{ width: 400 }}
                />
                <p className="text-green-600 mt-2 font-semibold text-lg">
                  ลงนามเมื่อ {formatThaiDate(contract?.Sign_Date)}
                </p>

                {contract?.Sign_Remark && (
                  <p className="text-gray-600 mt-1">หมายเหตุ: {contract?.Sign_Remark}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
